import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import Pagination from '../components/Pagination';
import SearchableSelect from '../../shared/SearchableSelect';
import { exportToCSV } from '../exportUtils';
import { exportReportToExcel, exportReportToPDF, buildReportFileName } from './reportExport';
import { getErrorMessage } from '../../api/api';
import '../lib-page-header.css';
import './Reports.css';

const PAGE_SIZE = 20;
// Safety bound for "export all filtered rows" — matches the backend's
// raised report page_size cap (see models paginate()). On-screen browsing
// always uses PAGE_SIZE (20); this is only used by the export handlers.
const EXPORT_MAX_ROWS = 5000;

/**
 * One shared, config-driven shell for every centralized report (see
 * reportConfigs.js). Handles: an optional entity picker (Member/Book —
 * required before a per-entity report can fetch anything), a search box,
 * a filter row built from config.filters, Reset, server-side pagination
 * (via the shared ../components/Pagination), the standard
 * "Search | Reset | Export Excel | Export PDF | Print" action bar, and a
 * print-friendly output block — so no individual report re-implements any
 * of this.
 *
 * config shape (see reportConfigs.js for full examples):
 *   title, subtitle, icon, printTitle, fileNamePrefix
 *   filters: [{ key, label, type: 'date'|'select', options? }]
 *   searchPlaceholder?: string — omit to hide the free-text search box
 *   entityPicker?: { label, placeholder, searchFn(libraryId, term), getOptionValue, getOptionLabel, renderOption? }
 *   columns: [{ key, header, render?(row) }]              — on-screen table
 *   exportColumns: [{ key, header }]                       — plain-text CSV/Excel/PDF columns
 *   fetchFn(libraryId, entityId|null, params) -> API envelope { data, meta?, totals? }
 *   paginated?: boolean (default true)
 *   renderTotals?(totals) -> ReactNode
 *   pdfOrientation?: 'portrait'|'landscape' (default 'landscape')
 *   emptyMessage?: string
 */
const ReportPage = ({ config }) => {
  const libraryId = useActiveLibraryId();
  const sectionBase = useSectionBase();

  const [draftFilters, setDraftFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [draftSearch, setDraftSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [entityId, setEntityId] = useState('');
  const [entitySearchResults, setEntitySearchResults] = useState([]);
  const [entitySearchLoading, setEntitySearchLoading] = useState(false);

  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [loadedFilterOptions, setLoadedFilterOptions] = useState({});
  const showLoader = useMinLoading(loading, 600);

  // Some filters (e.g. Member Category) come from master/config data instead
  // of a static list — config.filters marks them with optionsLoader, and
  // this loads each one once and swaps it in below, falling back to the
  // config's static `options` (if any) until it resolves or if it fails.
  useEffect(() => {
    (config.filters || []).forEach((filter) => {
      if (!filter.optionsLoader) return;
      // libraryId is passed for library-scoped loaders (e.g. Most Issued
      // Books' category filter); existing loaders like getMemberCategoryOptions
      // take no params and simply ignore the extra argument.
      const result = filter.optionsLoader(libraryId);
      // Guards against a loader that isn't a promise (e.g. an incomplete
      // test mock) — every real optionsLoader (getMemberCategoryOptions)
      // always returns one, so this never changes production behaviour.
      if (!result || typeof result.then !== 'function') return;
      result
        .then((opts) => setLoadedFilterOptions((prev) => ({ ...prev, [filter.key]: opts || [] })))
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, libraryId]);

  // Lets another report's row-level link (e.g. Issues by Member Category's
  // Total Issues drill-down) open this report pre-filtered, by putting the
  // filter values in the query string. Read straight from window.location
  // (not a react-router hook, so this needs no route/provider changes)
  // once on mount only, so a normal visit with no query string behaves
  // exactly as before.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const seeded = {};
    params.forEach((value, key) => {
      if (!value) return;
      if (key === 'from_date' || key === 'to_date' || (config.filters || []).some((f) => f.key === key)) {
        seeded[key] = value;
      }
    });
    if (Object.keys(seeded).length > 0) {
      setDraftFilters(seeded);
      setAppliedFilters(seeded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requiresEntity = !!config.entityPicker;
  const canFetch = !requiresEntity || !!entityId;

  const fetchReport = async () => {
    if (!libraryId || !canFetch) {
      setResponse(null);
      return;
    }

    const params = { page, page_size: PAGE_SIZE, search: appliedSearch || undefined, ...appliedFilters };
    Object.keys(params).forEach((key) => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
    });

    try {
      setLoading(true);
      setError('');
      const res = requiresEntity
        ? await config.fetchFn(libraryId, entityId, params)
        : await config.fetchFn(libraryId, params);

      if (!res) return;
      if (res.success === false) {
        setError(res.error || 'Failed to load report');
        setResponse(null);
        return;
      }
      setResponse(res);
    } catch (err) {
      setError(getErrorMessage(err));
      setResponse(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId, entityId, page, appliedFilters, appliedSearch]);

  const handleEntitySearch = async (term) => {
    if (!config.entityPicker || !libraryId) return;
    try {
      setEntitySearchLoading(true);
      const results = await config.entityPicker.searchFn(libraryId, term);
      setEntitySearchResults(results || []);
    } catch (err) {
      setEntitySearchResults([]);
      Swal.fire('Error', getErrorMessage(err), 'error');
    } finally {
      setEntitySearchLoading(false);
    }
  };

  const entityOptions = useMemo(() => {
    if (!config.entityPicker) return [];
    return entitySearchResults.map((item) => ({
      value: config.entityPicker.getOptionValue(item),
      label: config.entityPicker.getOptionLabel(item),
      item
    }));
  }, [config.entityPicker, entitySearchResults]);

  const handleSearchClick = () => {
    setPage(1);
    setAppliedSearch(draftSearch);
    setAppliedFilters(draftFilters);
  };

  const handleReset = () => {
    setDraftSearch('');
    setAppliedSearch('');
    setDraftFilters({});
    setAppliedFilters({});
    setPage(1);
  };

  const rows = response?.data || [];
  const rowsForBody = Array.isArray(rows) ? rows : [];
  const meta = response?.meta;
  const totals = response?.totals;
  const paginated = config.paginated !== false && !!meta;

  const buildExportRows = (sourceRows) =>
    sourceRows.map((row) =>
      config.exportColumns.reduce((acc, c) => {
        acc[c.key] = c.render ? c.render(row) : row[c.key] ?? '';
        return acc;
      }, {})
    );

  // Exports must reflect the complete filtered result set, not just the
  // ≤20 rows currently on screen. Non-paginated (aggregate "Top N") reports
  // already hold their full bounded result set in rowsForBody. Paginated
  // reports re-fetch once with a page_size large enough to cover every
  // matching row (bounded by EXPORT_MAX_ROWS), reusing the exact same
  // fetchFn/filters/search as the on-screen query — same query/business
  // logic, just without the on-screen page-size limit.
  const fetchAllRowsForExport = async () => {
    if (!paginated) return rowsForBody;
    if (!meta || !meta.totalItems) return rowsForBody;
    if (meta.totalItems <= rowsForBody.length && meta.page === 1) return rowsForBody;

    const params = {
      page: 1,
      page_size: Math.min(meta.totalItems, EXPORT_MAX_ROWS),
      search: appliedSearch || undefined,
      ...appliedFilters
    };
    Object.keys(params).forEach((key) => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
    });

    const res = requiresEntity
      ? await config.fetchFn(libraryId, entityId, params)
      : await config.fetchFn(libraryId, params);

    if (!res || res.success === false) {
      throw new Error(res?.error || 'Failed to load the complete filtered data set for export');
    }
    return Array.isArray(res.data) ? res.data : [];
  };

  const filtersSummaryText = () => {
    const parts = [];
    if (appliedFilters.from_date || appliedFilters.to_date) {
      parts.push(`Date: ${appliedFilters.from_date || 'Any'} to ${appliedFilters.to_date || 'Any'}`);
    }
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (key === 'from_date' || key === 'to_date' || !value) return;
      const filterDef = config.filters?.find((f) => f.key === key);
      const availableOptions = (filterDef?.optionsLoader ? loadedFilterOptions[key] : null) || filterDef?.options;
      const optionLabel = availableOptions?.find((o) => String(o.value) === String(value))?.label;
      parts.push(`${filterDef?.label || key}: ${optionLabel || value}`);
    });
    if (appliedSearch) parts.push(`Search: "${appliedSearch}"`);
    return parts.join(' | ');
  };

  const runExport = async (exportFn) => {
    if (rowsForBody.length === 0) {
      Swal.fire('No Data', 'There is nothing to export for the current filters.', 'info');
      return;
    }
    try {
      setExporting(true);
      const allRows = await fetchAllRowsForExport();
      if (allRows.length === 0) {
        Swal.fire('No Data', 'There is nothing to export for the current filters.', 'info');
        return;
      }
      exportFn(allRows);
    } catch (err) {
      Swal.fire('Export Failed', getErrorMessage(err), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = () =>
    runExport((allRows) => {
      const fileName = buildReportFileName(config.fileNamePrefix, appliedFilters.from_date, appliedFilters.to_date);
      exportReportToExcel(config.exportColumns, buildExportRows(allRows), fileName, config.title);
    });

  const handleExportCSV = () =>
    runExport((allRows) => {
      const fileName = buildReportFileName(config.fileNamePrefix, appliedFilters.from_date, appliedFilters.to_date);
      exportToCSV(config.exportColumns, buildExportRows(allRows), fileName);
    });

  const handleExportPDF = () =>
    runExport((allRows) => {
      const fileName = buildReportFileName(config.fileNamePrefix, appliedFilters.from_date, appliedFilters.to_date);
      exportReportToPDF(config.exportColumns, buildExportRows(allRows), fileName, {
        title: config.printTitle || config.title,
        subtitle: `Generated by Community Library Management System`,
        filtersText: filtersSummaryText(),
        orientation: config.pdfOrientation || 'landscape',
        totalsText: config.renderTotalsText ? config.renderTotalsText(totals) : ''
      });
    });

  const handlePrint = () => window.print();

  return (
    <div className="container-fluid mt-3 rpt-page">
      <style>
        {`
          @media print {
            @page { size: A4 ${config.pdfOrientation === 'portrait' ? 'portrait' : 'landscape'}; margin: 10mm; }
            body * { visibility: hidden; }
            .rpt-print-area, .rpt-print-area * { visibility: visible; }
            .rpt-print-area { position: absolute; left: 0; top: 0; width: 100%; }
            .rpt-no-print { display: none !important; }
          }
        `}
      </style>

      <div className="rpt-no-print">
        <div className="lib-page-header">
          <div className="lib-page-header__left">
            <div className="lib-page-header__icon">
              <i className={config.icon || 'fas fa-chart-bar'} />
            </div>
            <div>
              <h3 className="lib-page-header__title">{config.title}</h3>
              <div className="lib-page-header__subtitle">{config.subtitle}</div>
            </div>
          </div>
          <div className="lib-page-header__actions">
            <Link to={`${sectionBase}/reports`} className="btn btn-outline-secondary btn-sm">
              <i className="fas fa-arrow-left me-1" /> All Reports
            </Link>
          </div>
        </div>

        <div className="card shadow-sm table-full-card">
          <div className="card-body">
            {config.entityPicker && (
              <div className="rpt-entity-picker mb-3">
                <label className="form-label">{config.entityPicker.label}</label>
                <SearchableSelect
                  name="entity_id"
                  value={entityId}
                  onChange={(e) => { setEntityId(e.target.value); setPage(1); }}
                  onSearchChange={handleEntitySearch}
                  options={entityOptions}
                  renderOption={config.entityPicker.renderOption}
                  loading={entitySearchLoading}
                  loadingMessage="Loading…"
                  placeholder={config.entityPicker.placeholder}
                  noOptionsMessage={entitySearchLoading ? 'Loading…' : 'No results found'}
                />
                {!entityId && (
                  <div className="small text-muted mt-1">Select {config.entityPicker.label.toLowerCase()} to view this report.</div>
                )}
              </div>
            )}

            {canFetch && (
              <>
                <div className="rpt-filter-row mb-3">
                  {config.searchPlaceholder && (
                    <input
                      type="text"
                      className="form-control rpt-search-input"
                      placeholder={config.searchPlaceholder}
                      value={draftSearch}
                      onChange={(e) => setDraftSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearchClick(); }}
                    />
                  )}

                  {(config.filters || []).map((filter) => (
                    <div className="rpt-filter-item" key={filter.key}>
                      {filter.type === 'date' ? (
                        <input
                          type="date"
                          className="form-control"
                          aria-label={filter.label}
                          value={draftFilters[filter.key] || ''}
                          onChange={(e) => setDraftFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))}
                        />
                      ) : filter.type === 'text' || filter.type === 'number' ? (
                        <input
                          type={filter.type}
                          className="form-control"
                          placeholder={filter.label}
                          aria-label={filter.label}
                          value={draftFilters[filter.key] || ''}
                          onChange={(e) => setDraftFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSearchClick(); }}
                        />
                      ) : (
                        <SearchableSelect
                          name={filter.key}
                          value={draftFilters[filter.key] || ''}
                          onChange={(e) => setDraftFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))}
                          options={filter.optionsLoader ? (loadedFilterOptions[filter.key] || filter.options || []) : filter.options}
                          placeholder={filter.label}
                        />
                      )}
                    </div>
                  ))}

                  <button type="button" className="btn btn-primary rpt-btn" onClick={handleSearchClick}>
                    <i className="fas fa-search me-1" /> Search
                  </button>
                  <button type="button" className="btn btn-outline-secondary rpt-btn" onClick={handleReset}>
                    <i className="fas fa-redo me-1" /> Reset
                  </button>

                  <div className="rpt-export-actions">
                    <button type="button" className="btn btn-outline-success rpt-btn" onClick={handleExportExcel} disabled={exporting}>
                      <i className="fas fa-file-excel me-1" /> Excel
                    </button>
                    <button type="button" className="btn btn-outline-danger rpt-btn" onClick={handleExportPDF} disabled={exporting}>
                      <i className="fas fa-file-pdf me-1" /> PDF
                    </button>
                    <button type="button" className="btn btn-outline-secondary rpt-btn" onClick={handleExportCSV} disabled={exporting}>
                      <i className="fas fa-file-csv me-1" /> CSV
                    </button>
                    <button type="button" className="btn btn-outline-primary rpt-btn" onClick={handlePrint}>
                      <i className="fas fa-print me-1" /> Print
                    </button>
                    {exporting && <span className="small text-muted ms-2">Preparing export…</span>}
                  </div>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {showLoader ? (
                  <Loader label="Loading report…" />
                ) : (
                  <div className="rpt-print-area">
                    <div className="rpt-print-header d-none d-print-block">
                      <h4>Community Library Management System</h4>
                      <h5>{config.printTitle || config.title}</h5>
                      <div className="small">{filtersSummaryText()}</div>
                      <div className="small text-muted">Generated: {new Date().toLocaleString('en-IN')}</div>
                    </div>

                    {config.renderTotals && totals && (
                      <div className="rpt-totals mb-3">{config.renderTotals(totals)}</div>
                    )}

                    {config.renderBody ? (
                      config.renderBody({ data: response?.data, loading, meta })
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle lib-table-cell">
                          <thead className="table-light lib-table-header">
                            <tr>
                              {config.columns.map((col) => (
                                <th key={col.key}>{col.header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rowsForBody.length > 0 ? (
                              rowsForBody.map((row, index) => (
                                <tr key={row.id || row.fine_id || row.book_copy_id || row.book_id || row.member_id || index}>
                                  {config.columns.map((col) => (
                                    <td key={col.key} data-label={col.header}>
                                      {col.render ? col.render(row) : row[col.key] ?? '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={config.columns.length} className="text-center text-muted py-4">
                                  {config.emptyMessage || 'No records found'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {paginated && (
                      <div className="rpt-no-print">
                        <Pagination
                          page={meta.page}
                          totalPages={meta.totalPages}
                          totalItems={meta.totalItems}
                          itemLabel="records"
                          onPageChange={setPage}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
